/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { FallbackPolicyType } from '../../../../../../../service/gen/templates';
import { getAppHost } from '../../../../../../../utilities/TeamsUtils';
import {
    IPersonaProps2PeopleResults,
    PeopleResults2IPersonaProps,
    UI_FallbackPolicyInput,
} from '../../../../../../../utilities/TemplateUtils';
import { stringFormat } from '../../../../../../../utilities/Utils';
import { TemplatePeoplePicker } from '../../../../../TemplatePeoplePicker';
import { DataSourceType } from '../../../../../TemplatePeoplePicker/TemplatePeoplePicker.container';
import { getStackStyles, getTextStyles } from './FallbackPolicy.styles';

export interface FallbackPolicyComponentProps {
    label?: string;
    inline?: boolean;
    fallbackPolicy: UI_FallbackPolicyInput;
    onFallbackPolicyChange?: (newVal: UI_FallbackPolicyInput) => void;
}

export const FallbackPolicyComponent: React.FC<FallbackPolicyComponentProps> = ({
    label,
    fallbackPolicy,
    onFallbackPolicyChange,
    inline,
}: FallbackPolicyComponentProps): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    /* Fallback policy vars/states */
    const fallbackPolicyOptions: IDropdownOption[] = [
        { key: FallbackPolicyType.FallbackApprovers, text: loc.strings.specify_approvers_option_text },
        { key: FallbackPolicyType.LetUserEnter, text: loc.strings.let_submitter_enter_option_text },
    ];
    const [fallbackPolicyError, setFallbackPolicyError] = React.useState<string>(null);

    const validateFallbackApprovers = (fallbackPolicy: UI_FallbackPolicyInput): void => {
        switch (fallbackPolicy?.Type) {
            case FallbackPolicyType.LetUserEnter: {
                setFallbackPolicyError(null);
                break;
            }
            case FallbackPolicyType?.FallbackApprovers: {
                const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
                if (fallbackPolicy.FallbackApprovers.length === 0) {
                    setFallbackPolicyError(loc.strings.required);
                } else if (fallbackPolicy.FallbackApprovers.length > maxNumberOfApprovers) {
                    setFallbackPolicyError(loc.strings.maximum_approvers_error);
                } else {
                    setFallbackPolicyError(null);
                }
            }
        }
    };

    const onFallbackPolicyTypeChange = (event: any, option?: IDropdownOption): void => {
        const newFallbackPolicy: UI_FallbackPolicyInput = { ...fallbackPolicy };
        newFallbackPolicy.Type = option.key as FallbackPolicyType;

        onFallbackPolicyChange(newFallbackPolicy);
    };

    const onFallbackApproverChange = (newApproversPersona: IPersonaProps[]): void => {
        const newFallbackPolicy: UI_FallbackPolicyInput = {
            ...fallbackPolicy,
            FallbackApprovers: IPersonaProps2PeopleResults(newApproversPersona),
        };
        if (!newApproversPersona || (newApproversPersona.length < 2 && !inline)) {
            newFallbackPolicy.FallbackApproversAllMustRespond = true;
        }
        validateFallbackApprovers(newFallbackPolicy);
        onFallbackPolicyChange(newFallbackPolicy);
    };

    const onFallbackApproverBlur = (): void => {
        validateFallbackApprovers(fallbackPolicy);
    };

    const onFallbackApproversAllMustRespondChange = (event: any, newVal?: boolean): void => {
        const newFallbackPolicy: UI_FallbackPolicyInput = { ...fallbackPolicy };
        newFallbackPolicy.FallbackApproversAllMustRespond = newVal;

        onFallbackPolicyChange(newFallbackPolicy);
    };

    return (
        <Stack styles={getStackStyles}>
            {label && (
                <Text block nowrap styles={getTextStyles(theme)}>
                    {label}
                </Text>
            )}
            <Stack horizontal>
                <Dropdown
                    responsiveMode={ResponsiveMode.large}
                    options={fallbackPolicyOptions}
                    onChange={onFallbackPolicyTypeChange}
                    selectedKey={fallbackPolicy?.Type}
                />
                {fallbackPolicy?.Type === FallbackPolicyType.FallbackApprovers && (
                    <TemplatePeoplePicker
                        hideLabel
                        label={loc.strings.visibility_scope_column_header}
                        onApproversSelected={onFallbackApproverChange}
                        error={fallbackPolicyError}
                        currentSelectedItems={PeopleResults2IPersonaProps(fallbackPolicy.FallbackApprovers)}
                        dataSourceType={DataSourceType.TenantWidePeople}
                        onBlur={onFallbackApproverBlur}
                        required
                    />
                )}
            </Stack>

            {fallbackPolicy?.Type === FallbackPolicyType.FallbackApprovers && !inline && (
                <Toggle
                    disabled={fallbackPolicy.FallbackApprovers?.length < 2}
                    checked={fallbackPolicy.FallbackApproversAllMustRespond}
                    label={loc.strings.all_must_respond_toggle_label}
                    inlineLabel
                    onChange={onFallbackApproversAllMustRespondChange}
                    ariaLabel={stringFormat(loc.strings.all_must_approve_aria_label, {
                        state: fallbackPolicy.FallbackApproversAllMustRespond
                            ? loc.strings.checked_aria_label
                            : loc.strings.unchecked_aria_label,
                    })}
                />
            )}
        </Stack>
    );
};
